/**
 * AI 决策接口 + 基础实现
 *
 * 分阶段实现：
 *   · RandomAI   —— 完全随机（用于集成测试 / 跑通流程）
 *   · EfficientAI —— 牌效优先（向听数驱动）★ 阶段 2 主体
 *   · DefensiveAI —— 牌效 + 防守（后续）
 */

import {
  COPIES_PER_TILE,
  indexToName,
  tileIndex,
  type Tile,
} from '../tile.js';
import { shanten } from '../shanten.js';
import { analyzeWaits } from '../wait.js';
import { isAgari } from '../agari.js';
import type { Action, GameState, Seat } from './state.js';
import { handToCounts, remainingCount, canRiichiWithDiscard, canRiichi } from './rules.js';
import { analyzeFuriten, type FuritenTracker } from './furiten.js';

// ════════════════════════════════════════════════════════════════
// AI 接口
// ════════════════════════════════════════════════════════════════

/** AI 决策上下文 */
export interface AIContext {
  readonly state: GameState;
  readonly seat: Seat;
  readonly tracker?: FuritenTracker;
  /** 随机源（AI 内部需要随机时用） */
  readonly random?: () => number;
}

/** AI 接口 */
export interface AI {
  /** AI 名称 */
  readonly name: string;
  /** 选择要打的牌 */
  discard(ctx: AIContext): Tile;
  /** 是否宣言立直（返回要打的牌 + 立直标记） */
  riichi?(ctx: AIContext): { tile: Tile; riichi: boolean } | null;
  /** 对打出的牌做出响应（鸣牌/和牌/pass） */
  respond(ctx: AIContext, tile: Tile, fromSeat: Seat): Action;
  /** 是否自摸和牌 */
  shouldTsumo(ctx: AIContext): boolean;
  /** 是否暗杠/加杠（返回要杠的牌） */
  kan?(ctx: AIContext): Tile | null;
}

// ════════════════════════════════════════════════════════════════
// 随机 AI（跑通用）
// ════════════════════════════════════════════════════════════════

/** 完全随机的 AI —— 用于集成测试 */
export class RandomAI implements AI {
  readonly name = 'RandomAI';

  constructor(private readonly rand: () => number = Math.random) {}

  discard(ctx: AIContext): Tile {
    const hand = ctx.state.players[ctx.seat].hand;
    // 摸牌后 14 张，随机打一张
    const idx = Math.floor(this.rand() * hand.length);
    return hand[idx];
  }

  respond(ctx: AIContext, tile: Tile, fromSeat: Seat): Action {
    const hand = ctx.state.players[ctx.seat].hand;
    const idx = tileIndex(tile);

    // 20% 概率和牌（如果满足条件）
    const testHand = [...hand, tile];
    if (isAgari(handToCounts(testHand), ctx.state.players[ctx.seat].melds.length)) {
      if (this.rand() < 0.8) return { type: 'ron' };
    }

    // 10% 概率碰
    const count = hand.filter((t) => tileIndex(t) === idx).length;
    if (count >= 2 && this.rand() < 0.1) {
      const tiles = hand.filter((t) => tileIndex(t) === idx).slice(0, 2);
      return { type: 'pon' };
    }

    return { type: 'pass' };
  }

  shouldTsumo(ctx: AIContext): boolean {
    const p = ctx.state.players[ctx.seat];
    return isAgari(handToCounts(p.hand), p.melds.length);
  }
}

// ════════════════════════════════════════════════════════════════
// 牌效 AI（阶段 2 主体）
// ════════════════════════════════════════════════════════════════

/** 牌效 AI 配置 */
export interface EfficientAIConfig {
  /** 是否立直（false = 默听） */
  readonly declareRiichi: boolean;
  /** 是否鸣牌 */
  readonly callMelds: boolean;
  /** 副露后是否仍追求和牌（false = 门清优先） */
  readonly keepClosed: boolean;
}

export const DEFAULT_EFFICIENT_CONFIG: EfficientAIConfig = {
  declareRiichi: true,
  callMelds: true,
  keepClosed: true,
};

/**
 * 牌效 AI
 *
 * 策略：
 *   1. 打牌：选使"向听数最小 + 有效进张最多"的牌
 *   2. 立直：听牌且门清时宣言
 *   3. 鸣牌：仅当能降低向听数且不破坏门清（可配置）
 *   4. 和牌：满足条件即和
 */
export class EfficientAI implements AI {
  readonly name = 'EfficientAI';

  constructor(
    private readonly config: EfficientAIConfig = DEFAULT_EFFICIENT_CONFIG,
    private readonly rand: () => number = Math.random,
  ) {}

  discard(ctx: AIContext): Tile {
    return chooseBestDiscard(ctx.state, ctx.seat);
  }

  riichi(ctx: AIContext): { tile: Tile; riichi: boolean } | null {
    if (!this.config.declareRiichi) return null;
    const state = ctx.state;
    const player = state.players[ctx.seat];

    // 已立直 / 有副露 / 点数不足 → 不立直
    if (!canRiichi(state).legal) return null;

    // 找能保持听牌的打牌
    const options = [...player.hand].filter((t) =>
      canRiichiWithDiscard(state, t).legal,
    );
    if (options.length === 0) return null;

    // 选最好的（向听数最小 → 有效牌最多）
    const best = options.reduce((a, b) =>
      discardScore(state, ctx.seat, b) > discardScore(state, ctx.seat, a) ? b : a,
    );
    return { tile: best, riichi: true };
  }

  respond(ctx: AIContext, tile: Tile, fromSeat: Seat): Action {
    const state = ctx.state;
    const player = state.players[ctx.seat];

    // 1. 能荣和就和（检查振听）
    const testHand = [...player.hand, tile];
    if (isAgari(handToCounts(testHand), player.melds.length)) {
      const furiten = ctx.tracker
        ? analyzeFuriten(player, ctx.tracker).furiten
        : false;
      if (!furiten) return { type: 'ron' };
    }

    if (!this.config.callMelds) return { type: 'pass' };
    if (player.riichi) return { type: 'pass' };

    // 2. 碰（仅当能降低向听数）
    const idx = tileIndex(tile);
    const count = player.hand.filter((t) => tileIndex(t) === idx).length;
    if (count >= 2) {
      const before = shanten(handToCounts(player.hand), player.melds.length);
      const after = simulatePon(player.hand, idx, player.melds.length);
      // 碰后手牌少 2 张，需要摸牌才能比较；简化：用"碰后向听数 <= 碰前"判断
      if (after <= before - 1) return { type: 'pon' };
    }

    // 3. 吃（仅当能降低向听数）
    const chiOptions = findChiCombinations(player.hand, idx);
    for (const combo of chiOptions) {
      const before = shanten(handToCounts(player.hand), player.melds.length);
      const after = simulateChi(player.hand, combo, player.melds.length);
      if (after <= before - 1) {
        return { type: 'chi', tiles: combo };
      }
    }

    return { type: 'pass' };
  }

  shouldTsumo(ctx: AIContext): boolean {
    const p = ctx.state.players[ctx.seat];
    return isAgari(handToCounts(p.hand), p.melds.length);
  }

  kan(ctx: AIContext): Tile | null {
    const p = ctx.state.players[ctx.seat];
    const counts = handToCounts(p.hand);
    // 有 4 张就杠（简化：不做听牌形态判断）
    for (let i = 0; i < 34; i++) {
      if (counts[i] === 4) {
        return p.hand.find((t) => tileIndex(t) === i) ?? null;
      }
    }
    return null;
  }
}

// ════════════════════════════════════════════════════════════════
// 打牌选择（牌效核心）
// ════════════════════════════════════════════════════════════════

/**
 * 为某张牌打分（越高越好）
 *
 * 评分 = 向听数优先，其次有效进张数
 */
function discardScore(state: GameState, seat: Seat, tile: Tile): number {
  const player = state.players[seat];
  const after = player.hand.filter((t) => t.id !== tile.id);
  const counts = handToCounts(after);
  const meldCount = player.melds.length;

  const s = shanten(counts, meldCount);
  // 向听数越低越好（用负值 + 大偏移）
  const shantenScore = (8 - s) * 1000;

  // 有效进张数（仅当手牌 13 张时可算）
  let ukeire = 0;
  if (after.length === 13) {
    for (let i = 0; i < 34; i++) {
      if (counts[i] >= COPIES_PER_TILE) continue;
      const test = [...counts];
      test[i]++;
      if (shanten(test, meldCount) < s) {
        ukeire += remainingCount(state, seat, i);
      }
    }
  }

  return shantenScore + ukeire;
}

/** 选择最优打牌 */
export function chooseBestDiscard(state: GameState, seat: Seat): Tile {
  const player = state.players[seat];
  const hand = player.hand;
  if (hand.length === 0) throw new Error('手牌为空');

  // 去重（同种牌只算一次）
  const seen = new Set<number>();
  let best: Tile | null = null;
  let bestScore = -Infinity;

  for (const tile of hand) {
    const idx = tileIndex(tile);
    if (seen.has(idx)) continue;
    seen.add(idx);

    const score = discardScore(state, seat, tile);
    if (score > bestScore) {
      bestScore = score;
      best = tile;
    }
  }
  return best!;
}

// ════════════════════════════════════════════════════════════════
// 鸣牌模拟（判断鸣牌是否降低向听）
// ════════════════════════════════════════════════════════════════

/** 模拟碰之后的向听数 */
function simulatePon(hand: readonly Tile[], idx: number, meldCount: number): number {
  const removed = hand.filter((t) => tileIndex(t) === idx).slice(0, 2);
  const remaining = hand.filter((t) => !removed.some((r) => r.id === t.id));
  const counts = handToCounts(remaining);
  // 碰后少 2 张（13 → 11），补一张虚拟牌才能比较向听
  // 简化：直接算 11 张的向听数（相当于 −1 张）
  return shanten(counts, meldCount + 1);
}

/** 模拟吃之后的向听数 */
function simulateChi(
  hand: readonly Tile[],
  combo: readonly Tile[],
  meldCount: number,
): number {
  const remaining = hand.filter((t) => !combo.some((c) => c.id === t.id));
  const counts = handToCounts(remaining);
  return shanten(counts, meldCount + 1);
}

/** 找出手牌中能与某张牌组成顺子的组合 */
export function findChiCombinations(
  hand: readonly Tile[],
  tileIdx: number,
): Tile[][] {
  const results: Tile[][] = [];
  const counts = handToCounts(hand);
  const suitStart = Math.floor(tileIdx / 9) * 9;
  const pos = tileIdx - suitStart;
  if (tileIdx >= 27) return results; // 字牌不能吃

  // 三种可能：(pos-2,pos-1) (pos-1,pos+1) (pos+1,pos+2)
  const patterns: Array<[number, number]> = [
    [pos - 2, pos - 1],
    [pos - 1, pos + 1],
    [pos + 1, pos + 2],
  ];

  for (const [a, b] of patterns) {
    if (a < 0 || b > 8) continue;
    const ia = suitStart + a;
    const ib = suitStart + b;
    if (counts[ia] > 0 && counts[ib] > 0) {
      const ta = [...hand].find((t) => tileIndex(t) === ia)!;
      // 如果 a === b（不可能，因为 a<b），需要两张
      const tb = [...hand].filter((t) => tileIndex(t) === ib).find((t) => t.id !== ta.id);
      if (tb) results.push([ta, tb]);
    }
  }
  return results;
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 描述打牌决策（调试） */
export function describeDiscard(state: GameState, seat: Seat, tile: Tile): string {
  const player = state.players[seat];
  const after = player.hand.filter((t) => t.id !== tile.id);
  const counts = handToCounts(after);
  const s = shanten(counts, player.melds.length);
  return `打 ${indexToName(tileIndex(tile))} → ${s} 向听`;
}


// ════════════════════════════════════════════════════════════════
// 押し引き AI（a3+a4）—— 牌效 + 防守 + 攻守判断
// ════════════════════════════════════════════════════════════════

/**
 * 押し引き AI
 *
 * 决策流程：
 *   1. 无威胁 → 纯牌效（最快成型）
 *   2. 有威胁 → decidePushFold：
 *        · push    → 牌效最优牌（冒险进攻）
 *        · partial → 安全牌里选牌效最好的
 *        · fold    → 最安全的牌
 *   3. 立直/鸣牌判断沿用 EfficientAI 的逻辑
 */
export class PushFoldAI implements AI {
  readonly name = 'PushFoldAI';

  constructor(
    private readonly config: EfficiencyAIConfig = DEFAULT_EFFICIENT_CONFIG,
    private readonly rand: () => number = Math.random,
  ) {}

  discard(ctx: AIContext): Tile {
    // 延迟引入，避免模块循环依赖
    const { decidePushFold } = pushfoldModule;
    const decision = decidePushFold(ctx.state, ctx.seat);
    return decision.tile;
  }

  /** 暴露决策详情（调试/教学用） */
  explain(ctx: AIContext) {
    const { decidePushFold } = pushfoldModule;
    return decidePushFold(ctx.state, ctx.seat);
  }

  riichi(ctx: AIContext): { tile: Tile; riichi: boolean } | null {
    if (!this.config.declareRiichi) return null;
    const state = ctx.state;
    const player = state.players[ctx.seat];
    if (!canRiichi(state).legal) return null;

    const options = [...player.hand].filter((t) => canRiichiWithDiscard(state, t).legal);
    if (options.length === 0) return null;

    const best = options.reduce((a, b) =>
      discardScore(state, ctx.seat, b) > discardScore(state, ctx.seat, a) ? b : a,
    );
    return { tile: best, riichi: true };
  }

  respond(ctx: AIContext, tile: Tile, fromSeat: Seat): Action {
    const state = ctx.state;
    const player = state.players[ctx.seat];

    // 能荣和就和（检查振听）
    const testHand = [...player.hand, tile];
    if (isAgari(handToCounts(testHand), player.melds.length)) {
      const furiten = ctx.tracker ? analyzeFuriten(player, ctx.tracker).furiten : false;
      if (!furiten) return { type: 'ron' };
    }

    if (!this.config.callMelds) return { type: 'pass' };
    if (player.riichi) return { type: 'pass' };

    // 押し引き：守势时不鸣牌
    const { decidePushFold } = pushfoldModule;
    const decision = decidePushFold(state, ctx.seat);
    if (decision.type === 'fold') return { type: 'pass' };

    // 碰（仅当能降低向听数）
    const idx = tileIndex(tile);
    const count = player.hand.filter((t) => tileIndex(t) === idx).length;
    if (count >= 2) {
      const before = shanten(handToCounts(player.hand), player.melds.length);
      const after = simulatePon(player.hand, idx, player.melds.length);
      if (after <= before - 1) return { type: 'pon' };
    }

    // 吃（仅当能降低向听数）
    for (const combo of findChiCombinations(player.hand, idx)) {
      const before = shanten(handToCounts(player.hand), player.melds.length);
      const after = simulateChi(player.hand, combo, player.melds.length);
      if (after <= before - 1) return { type: 'chi', tiles: combo };
    }

    return { type: 'pass' };
  }

  shouldTsumo(ctx: AIContext): boolean {
    const p = ctx.state.players[ctx.seat];
    return isAgari(handToCounts(p.hand), p.melds.length);
  }

  kan(ctx: AIContext): Tile | null {
    const p = ctx.state.players[ctx.seat];
    const counts = handToCounts(p.hand);
    for (let i = 0; i < 34; i++) {
      if (counts[i] === 4) return p.hand.find((t) => tileIndex(t) === i) ?? null;
    }
    return null;
  }
}

/** 延迟加载 pushfold（避免 ai ↔ pushfold 循环依赖） */
import * as _pushfold from './pushfold.js';
const pushfoldModule = _pushfold;
